// Functions to handle gift products from Shopify
import { getProducts, getProduct, addToCart as addToCartAPI, ShopifyProduct } from './shopify-api';
import { getGiftsForVariant, isFirstTimePurchase, GiftItem } from './gift-logic';

// Cache for gift products to avoid repeated API calls
let giftProductsCache: ShopifyProduct[] | null = null;

// Fetch all gift products from Shopify
export async function getGiftProducts(): Promise<ShopifyProduct[]> {
  if (giftProductsCache) return giftProductsCache;

  try {
    const allProducts = await getProducts();
    
    console.log('🔍 Checking for travel case in products...');
    const travelCase = allProducts.find(p => p.handle === 'olin-travel-case');
    if (travelCase) {
      console.log('✅ Found travel case:', travelCase.handle, 'Price:', travelCase.variants.edges[0]?.node.price);
    } else {
      console.log('❌ Travel case not found. Handles with "travel":', 
        allProducts.filter(p => p.handle.includes('travel')).map(p => p.handle));
    }
    
    // Filter for gift products (handle starts with 'olin-' and price is 0)
    const giftProducts = allProducts.filter(product => {
      const hasGiftHandle = ['olin-comb', 'olin-bag', 'olin-travel-case'].includes(product.handle);
      const isFreeProduct = product.variants.edges.some(variant => 
        parseFloat(variant.node.price.amount) === 0
      );
      if (hasGiftHandle) {
        console.log(`Gift check: ${product.handle} - hasHandle: ${hasGiftHandle}, isFree: ${isFreeProduct}`);
      }
      return hasGiftHandle && isFreeProduct;
    });

    console.log('🎁 Total gift products found:', giftProducts.map(p => p.handle));
    giftProductsCache = giftProducts;
    return giftProducts;
  } catch (error) {
    console.error('Error fetching gift products:', error);
    return [];
  }
}

// Get variant ID for a gift product by handle
export async function getGiftVariantId(giftHandle: string): Promise<string | null> {
  try {
    const giftProducts = await getGiftProducts();
    const giftProduct = giftProducts.find(p => p.handle === giftHandle);
    
    if (giftProduct && giftProduct.variants.edges.length > 0) {
      return giftProduct.variants.edges[0].node.id;
    }
    
    return null;
  } catch (error) {
    console.error(`Error getting variant ID for ${giftHandle}:`, error);
    return null;
  }
}

// Add gifts to cart for a specific product variant
export async function addGiftsToCart(
  cartId: string, 
  variantTitle: string, 
  customerEmail?: string
): Promise<void> {
  try {
    // Use the original first-time purchase detection - assume first time if no email
    const isFirstTime = await isFirstTimePurchase(customerEmail);
    
    console.log(`🔍 Gift check for ${customerEmail}: isFirstTime = ${isFirstTime}`);
    console.log(`🔍 Variant title: "${variantTitle}"`);
    const gifts = getGiftsForVariant(variantTitle, isFirstTime);
    console.log(`🎁 Expected gifts for "${variantTitle}":`, gifts);
    
    if (gifts.length === 0) {
      console.log('No gifts to add for this purchase');
      return;
    }

    // Get current cart to check if gifts already exist
    const { getCart } = await import('./shopify-api');
    const currentCart = await getCart(cartId);
    if (!currentCart) {
      console.error('Could not fetch current cart');
      return;
    }

    console.log(`Checking for existing gifts in cart...`);

    // Check which gifts are already in the cart
    const existingGifts = currentCart.lines.edges.filter(edge => {
      const handle = edge.node.merchandise.product.handle;
      const isGift = parseFloat(edge.node.cost.totalAmount.amount) === 0;
      return isGift && gifts.some(gift => gift.handle === handle);
    });

    const existingGiftHandles = existingGifts.map(edge => edge.node.merchandise.product.handle);
    const giftsToAdd = gifts.filter(gift => !existingGiftHandles.includes(gift.handle));

    if (giftsToAdd.length === 0) {
      console.log('All gifts already exist in cart - skipping gift addition');
      return;
    }

    console.log(`Adding ${giftsToAdd.length} new gifts (${existingGifts.length} already in cart):`, giftsToAdd.map(g => g.name));

    // Add only the missing gifts to the cart
    for (const gift of giftsToAdd) {
      console.log(`Attempting to add gift: ${gift.name} (handle: ${gift.handle})`);
      const variantId = await getGiftVariantId(gift.handle);
      if (variantId) {
        try {
          await addToCartAPI(cartId, variantId, 1);
          console.log(`✅ Successfully added gift: ${gift.name}`);
        } catch (error) {
          console.error(`❌ Failed to add gift ${gift.name}:`, error);
        }
      } else {
        console.error(`❌ Gift product not found in Shopify: ${gift.handle} (${gift.name})`);
        console.log('Available gift products should have handle:', gift.handle, 'and price: $0.00');
      }
    }
  } catch (error) {
    console.error('Error adding gifts to cart:', error);
  }
}

// Helper to get main product variant info for gift logic
export function getVariantTitle(product: ShopifyProduct, variantId: string): string {
  const variant = product.variants.edges.find(
    edge => edge.node.id === variantId
  );
  return variant?.node.title || '';
}

// Get gift handles associated with a main product
export function getGiftHandlesForProduct(productTitle: string): string[] {
  const title = productTitle.toLowerCase();
  
  // 30 day product (Olin Women/Woman) -> comb only
  if (
    title.includes('olin women') ||
    title.includes('olin woman')
  ) {
    return ['olin-comb'];
  }
  
  // 90 day product (Kit de 3 meses) -> comb + bag
  if (
    title.includes('kit de 3 meses') ||
    title.includes('3 meses')
  ) {
    return ['olin-comb', 'olin-bag'];
  }
  
  // Subscription (Suscripción Olin) -> comb + bag + travel case
  if (
    title.includes('suscripción olin') ||
    title.includes('suscripcion olin')
  ) {
    return ['olin-comb', 'olin-bag', 'olin-travel-case'];
  }

  return [];
}
